import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../../lib/utils";

/**
 * Horizontal scroll viewport with edge affordances.
 *
 * Renders a left/right scrim (gradient + chevron) that appears only while there
 * is hidden content on that side, signalling that the row can be scrolled
 * further. This avoids the "last item sits flush against the padding so the row
 * looks complete" trap (e.g. a 6th room section hidden past a 5-item viewport).
 *
 * The chevron is what makes the affordance reliable: a gradient alone fades
 * content into the background and is therefore invisible when, at rest, the
 * cut-off falls in the gap between items (nothing under the scrim to veil).
 *
 * `fadeFrom` MUST match the surrounding background colour so the scrim blends.
 */
export function ScrollFadeRow({
  children,
  className,
  fadeFrom = "from-white",
}: {
  children: ReactNode;
  className?: string;
  /** Tailwind `from-*` colour of the fade, matching the parent background. */
  fadeFrom?: string;
}): React.JSX.Element {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<{ start: boolean; end: boolean }>({
    start: false,
    end: false,
  });

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (viewport === null) return;
    const content = viewport.firstElementChild;

    function update(el: HTMLDivElement): void {
      const maxScroll = el.scrollWidth - el.clientWidth;
      const start = el.scrollLeft > 1;
      const end = el.scrollLeft < maxScroll - 1;
      setEdges((prev) =>
        prev.start === start && prev.end === end ? prev : { start, end }
      );
    }

    const onResizeOrScroll = (): void => {
      update(viewport);
    };
    update(viewport);
    viewport.addEventListener("scroll", onResizeOrScroll, { passive: true });
    // Observe both the viewport (orientation/width changes) and its content
    // (late layout: web-font load, async children) so detection is correct
    // before the user ever scrolls.
    const observer = new ResizeObserver(onResizeOrScroll);
    observer.observe(viewport);
    if (content !== null) observer.observe(content);
    return () => {
      viewport.removeEventListener("scroll", onResizeOrScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div className={cn("relative", className)}>
      <div ref={viewportRef} className="overflow-x-auto scrollbar-hidden">
        {children}
      </div>
      <div
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 flex w-20 items-start justify-start bg-linear-to-r from-50% to-transparent pl-5 text-ds-slate-500 transition-opacity",
          fadeFrom,
          edges.start ? "opacity-100" : "opacity-0"
        )}
      >
        <span className="flex h-12 items-center">
          <IconChevronLeft className="size-5 stroke-[1.5px]" />
        </span>
      </div>
      <div
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 flex w-20 items-start justify-end bg-linear-to-l from-50% to-transparent pr-5 text-ds-slate-500 transition-opacity",
          fadeFrom,
          edges.end ? "opacity-100" : "opacity-0"
        )}
      >
        <span className="flex h-12 items-center">
          <IconChevronRight className="size-5 stroke-[1.5px]" />
        </span>
      </div>
    </div>
  );
}
